import React, { useEffect, useRef, useState } from "react";
import {
  Bell,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  User,
  Link,
} from "lucide-react";
import LogoutButton from "./LogoutButton";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../store"; // adjust path if needed
import { fetchClients } from "../store/clientsSlice";
import type { Client } from "../api/clients";
import { Alerts } from "./types/alerts";
import { ApiResponse, getSelectedOwnerFromCookie, setSelectedOwnerCookie } from "@industronics/fids-utils";
import toast from "react-hot-toast";
import { Socket } from "socket.io-client";
import { useSocket } from "@industronics/socket";
import moment from "moment";
import { icons } from "../utils/icons";
import socketApi from "../utils/httpClient";

type HeaderProps = {
  isSidebarOpen: boolean;
  onSidebarToggle: (next: boolean) => void;
};

const Header: React.FC<HeaderProps> = ({ isSidebarOpen, onSidebarToggle }) => {
  const [isDropdownOpen, setProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // NEW — client dropdown state
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null); // applied
  const [pendingClientId, setPendingClientId] = useState<string | null>(null);  // in popup

  const profileRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [notificationList, setNotificationList] = useState<Alerts[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const PAGE_SIZE = 10;
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [hasMoreAlerts, setHasMoreAlerts] = useState(true);
  const [offset, setOffset] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  const getAlertId = (a: Alerts) => (a as any)._id ?? (a as any).id;
  const { socket } = useSocket();
  const seenIdsRef = useRef<Set<string>>(new Set());

  const clients = useSelector<RootState, Client[]>(
    (state) => state.clients.items
  );
  const clientsStatus = useSelector<RootState, string>(
    (state) => state.clients.status
  );
  const clientsLoading = clientsStatus === "loading";

  const selectedClient = clients.find((c) => c._id === selectedClientId || c._id === pendingClientId)
    || clients.find((c) => c._id === selectedClientId)
    || clients.find((c) => c._id === pendingClientId)
    || null;

  // ========== Browser Notification Click Handler ==========
  const handleBrowserNotificationClick = (alertData: any) => {
    // Close any open dropdowns
    setIsNotificationOpen(false);

    if (alertData.from === "device") {
      // Handle pending device registration
      if (alertData.meta?.status === "pending") {
        const deviceId = alertData.meta?.deviceId || alertData.userId;
        const serialNumber = alertData.meta?.serialNumber;

        // Dispatch custom event to filter pending device in DCMM
        window.dispatchEvent(new CustomEvent('filter-pending-device', {
          detail: {
            deviceId,
            serialNumber,
            alertId: alertData._id || alertData.id
          }
        }));

        // Navigate to devices page
        window.location.href = "/devices";
      }

      // Handle screenshot alerts
      if (alertData.meta?.screenshot?.file) {
        // Open screenshot viewer directly
        window.dispatchEvent(new CustomEvent('open-screenshot-viewer', {
          detail: {
            deviceId: alertData.meta?.deviceId || alertData.userId,
            screenshot: alertData.meta.screenshot,
            live: false // For existing screenshots
          }
        }));
      }
    }

    // Focus the window
    window.focus();
  };

  // Updated browser notification function with click handler
  const showBrowserNotification = (title: string, body: string, alertData?: any) => {
    if (Notification.permission === "granted") {
      const notification = new Notification(title, {
        body,
        icon: `/logo192.png`
      });

      // Add click handler to the notification
      notification.onclick = () => {
        if (alertData) {
          handleBrowserNotificationClick(alertData);
        }
        window.focus();
        notification.close();
      };
    }
  };

  // Handle scroll for collapsing effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close popovers on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (profileRef.current && !profileRef.current.contains(target))
        setProfileOpen(false);
      if (
        notificationRef.current &&
        !notificationRef.current.contains(target)
      )
        setIsNotificationOpen(false);
      if (locationRef.current && !locationRef.current.contains(target))
        setIsLocationOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load clients from API
  useEffect(() => {
    if (clientsStatus === "idle") {
      dispatch(fetchClients());
    }
  }, [dispatch, clientsStatus]);

  // Load selected client from localStorage
  useEffect(() => {
    const selectedOwner = getSelectedOwnerFromCookie();
    if (selectedOwner) {
      try {
        setSelectedClientId(selectedOwner);
        setPendingClientId(selectedOwner);
      } catch {
        // ignore
      }
    }
  }, []);

  const handleClientSelection = (clientId: string) => {
    setPendingClientId(clientId);
  };

  const hasChanges = () =>
    !!pendingClientId && pendingClientId !== selectedClientId;

  const handleApplyClick = () => {
    if (!pendingClientId) return;
    setSelectedClientId(pendingClientId);
    setSelectedOwnerCookie(pendingClientId);
    setIsLocationOpen(false);
    window.location.reload();
  };

  // Filter clients by search
  const filteredClients = clients.filter((client) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      client.clientName?.toLowerCase().includes(q) ||
      client.name?.toLowerCase().includes(q) ||
      client.email?.toLowerCase().includes(q)
    );
  });


  // Request browser push permission
  const enablePush = async () => {
    if (!("Notification" in window)) {
      console.warn("Browser does not support notifications");
      return;
    }
    if (Notification.permission !== "granted") {
      try {
        const result = await Notification.requestPermission();
        if (result !== "granted") {
          console.warn("Push permission not granted");
        }
      } catch (err) {
        console.error("Error requesting push permission:", err);
      }
    }
  };

  useEffect(() => {
    enablePush();
  }, []);

  const fetchAlerts = async (initial = false) => {
    if (isLoadingAlerts) return;
    if (!hasMoreAlerts && !initial) return;

    try {
      setIsLoadingAlerts(true);

      // if (initial) reset everything
      const nextOffset = initial ? 0 : offset;

      const res = await socketApi.get<ApiResponse>('/alerts', {
        params: { limit: PAGE_SIZE, offset: nextOffset },
      });

      const data: Alerts[] = Array.isArray(res.data) ? res.data : res.data?.data ?? []; // support either shape

      // de-dup using seenIds
      const fresh = data.filter((a) => {
        const id = getAlertId(a);
        if (!id) return true; // fallback if id missing
        if (seenIdsRef.current.has(id)) return false;
        seenIdsRef.current.add(id);
        return true;
      });

      setNotificationList(prev => initial ? fresh : [...prev, ...fresh]);

      // update hasMore + offset
      const received = data.length;
      setHasMoreAlerts(received === PAGE_SIZE);
      setOffset(nextOffset + received);
    } catch (e) {
      console.error('Failed to fetch alerts', e);
      toast.error('Failed to load alerts');
    } finally {
      setIsLoadingAlerts(false);
    }
  };

  // // when opening dropdown, reset and fetch first page
  useEffect(() => {
    if (isNotificationOpen) {
      setOffset(0);
      setHasMoreAlerts(true);
      seenIdsRef.current.clear();
      setNotificationList([]);
      fetchAlerts(true);
    }
  }, [isNotificationOpen]);

  const onAlertsScroll: React.UIEventHandler<HTMLUListElement> = (e) => {
    const el = e.currentTarget;
    const threshold = 60; // px from bottom
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      fetchAlerts();
    }
  };

  // ==========  Alert Click Handler ==========
  const handleAlertClick = (alert: Alerts) => {
    if (alert.from === "device") {
      // Handle pending device registration
      if (alert.meta?.status === "pending") {
        // Get device ID from alert
        const deviceId = alert.meta?.deviceId || alert.userId;
        const serialNumber = alert.meta?.serialNumber;

        // Dispatch custom event to filter pending device in DCMM
        window.dispatchEvent(new CustomEvent('filter-pending-device', {
          detail: {
            deviceId,
            serialNumber,
            alertId: getAlertId(alert)
          }
        }));

        // Navigate to devices page
        window.location.href = "/devices";
      }

      // Handle screenshot alerts
      if (alert.meta?.screenshot?.file) {
        // Open screenshot viewer directly
        window.dispatchEvent(new CustomEvent('open-screenshot-viewer', {
          detail: {
            deviceId: alert.meta?.deviceId || alert.userId,
            screenshot: alert.meta.screenshot,
            live: false // For existing screenshots
          }
        }));
      }
    }

    // Close notification dropdown after click
    setIsNotificationOpen(false);
  };

  useEffect(() => {
    if (!socket) return;

    const handleDeviceAlerts = (message: any) => {
      // Show browser notification with click handler
      showBrowserNotification(
        message.title || "Device Status Update",
        message.message || JSON.stringify(message)
        , message);
      // Update UI notification list
      setNotificationList(prev => {
        return [message, ...prev];
      });
      setUnreadCount((prev) => prev + 1);
    };

    const alertsListener = () => {
      socket.off("alert.created", handleDeviceAlerts);
      socket.on("alert.created", handleDeviceAlerts);
    };

    alertsListener();

    return () => {
      socket.off("alert.created", handleDeviceAlerts);
    };

  }, [socket]);


  const markRead = async () => {
    try {
      setTimeout(async () => {
        await socketApi.put(`/alerts`);
        setUnreadCount(0);
      }, 3000);
    } catch (e) {
      console.error('mark read failed', e);
    } finally {
    }
  };

  const getUnreadCounts = async () => {
    try {
      const repsonse = await socketApi.get<ApiResponse>(`/alerts/unread`);
      setUnreadCount(repsonse.data.data);
    } catch (e) {
      console.error('mark read failed', e);
    } finally {
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-white border-b border-gray-100 transition-all duration-300 ${isScrolled ? "shadow-md" : "shadow-sm"
        }`}
    >
      <nav className="top-0 z-50">
        <div
          className={`px-4 lg:px-6 flex items-center gap-4 transition-all duration-300 ${isScrolled ? "py-2" : "py-3"
            }`}
        >
          {/* Left: toggle + logo */}
          <div
            className={`flex items-center transition-all duration-300 ${isScrolled ? "gap-2" : "gap-3"
              }`}
          >
            <button
              onClick={() => onSidebarToggle(!isSidebarOpen)}
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-500 hover:text-white transition-all duration-300"
              aria-label="Toggle sidebar navigation"
            >
              {isSidebarOpen ? (
                <PanelLeftClose size={20} />
              ) : (
                <PanelLeftOpen size={20} />
              )}
            </button>

            <img
              src="/logo192.png"
              className={`transition-all duration-300 ${isScrolled ? "h-6 w-6" : "h-8 w-8"
                }`}
              alt="Logo"
            />
          </div>

          {/* Title - collapsing effect */}
          <div
            className={`flex flex-col ml-2 transition-all duration-300 ${isScrolled
              ? "opacity-0 scale-95 h-0 overflow-hidden"
              : "opacity-100 scale-100"
              }`}
          >
            <span className="text-3xl font-black italic text-[#0078c2] tracking-tight">
              <span className="text-sm font-medium text-gray-500 -mt-1 text-orange-400">
                Flight Information Display System{" "}
                <small className="text-xs italic text-blue-400">
                  (powered by IASB)
                </small>
              </span>
            </span>
          </div>


          {/* Right Side Controls */}
          <div
            className={`flex items-center ml-auto transition-all duration-300 ${isScrolled ? "space-x-2" : "space-x-3"
              }`}
          >
            {/* Small inline selected label (was Airport, now Client) */}

            {/* Client Selector (same UI spot as Airport selector) */}
            <div>
              <div style={{ cursor: "pointer" }}
                onClick={() => setIsLocationOpen((prev) => !prev)}
                className="flex items-center">
                <span className="ml-1">
                  {selectedClient
                    ? selectedClient.clientName || selectedClient.name
                    : ""}
                </span>
                <button
                  type="button"
                  className="relative inline-flex items-center p-2 text-sm font-medium text-center text-blue-600
                            hover:text-blue-800 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  aria-label="Select client"
                >
                  {/* reuse globe-style button as before */}
                  {/* If you had custom icons here, keep them; using Bell/Globe initially */}
                  {/* You can switch this to any icon you like */}
                  {/* Example: <Globe size={20} /> */}
                  <User size={20} />
                </button>
              </div>

              {/* Client dropdown (same structure as Airport dropdown) */}
              {isLocationOpen && (
                <div
                  ref={locationRef}
                  className="absolute mt-2 bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-100 right-24 z-50 w-72 overflow-hidden"
                >
                  <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
                    <p className="text-sm font-medium text-gray-700">
                      Select Client
                    </p>
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded">
                      {selectedClient
                        ? selectedClient.clientName || selectedClient.name
                        : "None"}
                    </span>
                  </div>

                  {/* Search */}
                  <div className="px-3 py-2">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg block w-full ps-8
                                    p-2 focus:outline-none focus:ring-1 focus:ring-blue-300"
                        placeholder="Search clients..."
                      />
                      <div className="absolute inset-y-0 start-0 flex items-center ps-2.5 pointer-events-none">
                        <span className="text-gray-400 w-4 h-4 text-xs">🔍</span>
                      </div>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400
                                        hover:text-gray-600"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Client list (replaces airport list) */}
                  <div className="max-h-80 overflow-y-auto border rounded-md">
                    <ul className="p-2">
                      {clientsLoading && (
                        <li className="px-2 py-2 text-xs text-gray-500">
                          Loading clients...
                        </li>
                      )}

                      {!clientsLoading && filteredClients.length === 0 && (
                        <li className="px-2 py-2 text-xs text-gray-500">
                          No clients found.
                        </li>
                      )}

                      {filteredClients.map((client) => (
                        <li className="px-2" key={client._id}>
                          <div
                            className={`flex items-center px-2 py-2 hover:bg-blue-50 rounded-md cursor-pointer
                                            ${pendingClientId === client._id
                                ? "bg-blue-50"
                                : ""
                              }`}
                            onClick={() =>
                              handleClientSelection(client._id)
                            }
                          >
                            <input
                              id={`client-${client._id}`}
                              type="radio"
                              name="client-selection"
                              checked={pendingClientId === client._id}
                              onChange={() =>
                                handleClientSelection(client._id)
                              }
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="ms-2 flex-grow">
                              <label
                                htmlFor={`client-${client._id}`}
                                className="text-sm font-medium text-gray-700 flex flex-col"
                              >
                                <span className="font-bold">
                                  {client.clientName || client.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {client.email}
                                </span>
                              </label>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="px-3 py-2 bg-gray-50 flex justify-end">
                    <button
                      disabled={!hasChanges()}
                      onClick={handleApplyClick}
                      className={`px-3 py-1.5 text-sm
                                    font-medium rounded-md ${hasChanges()
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Notifications */}
            {/* Notification Button */}
            <div ref={notificationRef}>
              <button type="button" onClick={() => {
                if (unreadCount) markRead();
                setIsNotificationOpen((prev) => !prev);

              }}
                className="relative inline-flex items-center p-2 text-sm font-medium text-center text-blue-600
                            hover:text-blue-800 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                aria-label="Notifications"
              >
                <icons.PI.PiBellRinging size={20} />
                {unreadCount > 0 && <div
                  className="absolute inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-red-500 rounded-full -top-1 -end-1">
                  {Math.min(unreadCount, 99)}
                </div>}
              </button>

              {/* Notification dropdown */}
              {isNotificationOpen && (
                <div
                  className="absolute w-80 mt-2 bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-100 right-16 z-50 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 flex justify-between items-center">
                    <p className="text-sm font-medium text-gray-700">Notifications</p>
                  </div>
                  <ul className="py-2 max-h-96 overflow-y-auto" onScroll={onAlertsScroll}>
                    {notificationList.map((noty, index) => (
                      <li className={`px-4 transition-colors duration-150 ${!noty.read ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`}
                        key={getAlertId(noty) ?? index}
                      >
                        <button
                          className="w-full py-3 text-left border-b border-gray-100 last:border-0"
                          onClick={() => handleAlertClick(noty)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-center space-x-2">
                              {/* Unread dot */}
                              {!noty.read ? (
                                <span className="h-2 w-2 rounded-full bg-blue-600 mt-1.5" />
                              ) : (
                                <span className="h-2 w-2 rounded-full bg-transparent mt-1.5" />
                              )}
                              <p className={`font-medium text-sm ${!noty.read ? 'text-blue-900' : 'text-gray-900'
                                }`}>
                                {noty.title}
                              </p>
                            </div>
                          </div>
                          <p className={`mt-1 text-sm pl-4 ${!noty.read ? 'text-blue-700' : 'text-gray-600'}`}>
                            {noty.message}
                          </p>
                          <p className="text-right font-light text-xs text-gray-500 mt-1">
                            {moment(noty.createdAt).format('DD MMM, HH:mm')}
                          </p>
                        </button>
                      </li>

                    ))}

                    {/* loader / end marker */}
                    <li className="px-4 py-3 text-center text-xs text-gray-500">
                      {isLoadingAlerts ? 'Loading…' : !hasMoreAlerts && notificationList.length > 0 ? 'No more alerts'
                        : null}
                    </li>
                  </ul>

                  <div className="px-4 py-2 bg-gray-50 text-center">
                    <Link to="/user/notification" className="text-sm font-medium text-blue-600 hover:text-blue-800">
                      View all notifications
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button
                className="flex items-center space-x-1 bg-blue-50 p-1.5 pl-2 pr-3 rounded-full hover:bg-blue-100 transition-colors"
                onClick={() => setProfileOpen((p) => !p)}
                aria-expanded={isDropdownOpen}
                aria-label="User menu"
              >
                <img
                  className={`transition-all duration-300 ${isScrolled ? "w-7 h-7" : "w-9 h-9"
                    }`}
                  src="https://api.dicebear.com/7.x/adventurer/svg?seed=guest"
                  alt="user"
                />
                <span
                  className={`text-sm font-medium text-gray-700 hidden md:block transition-all duration-300 ${isScrolled ? "text-xs" : "text-sm"
                    }`}
                >
                  Guest
                </span>
                <Settings size={18} className="text-blue-500" />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-100 z-50">
                  <div className="px-4 py-3 bg-gray-50">
                    <p className="text-sm font-bold text-gray-900">
                      Guest User
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      guest@example.com
                    </p>
                  </div>

                  <ul className="py-1">
                    <li>
                      <div className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                        <Settings size={18} className="mr-2 text-gray-500" />
                        User Settings
                      </div>
                    </li>
                    <li className="border-t border-gray-100">
                      <LogoutButton />
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
